import { useCallback, useRef, useState } from "react";
import * as Components from "./VideoPlayer.components";
import type { MuxPlayerProps as MuxPlayerDefaultProps } from "@mux/mux-player-react";
import MuxPlayer from "@mux/mux-player-react";
import theme from "components/MuiThemeProvider/theme";
import useResizeLayoutEffect from "hooks/useResizeLayoutEffect";
import { useTranslation } from "react-i18next";
import type { DivProps } from "components/primitives/Div";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";

interface BasePlayerProps {
  videoId: string;
  slotProps?: {
    root?: DivProps;
  };
}

export interface MuxPlayerProps extends MuxPlayerDefaultProps, BasePlayerProps {
  type: "mux";
}

export interface YoutubePlayerProps extends BasePlayerProps {
  type: "youtube";
}

export type VideoPlayerProps = MuxPlayerProps | YoutubePlayerProps;

function VideoPlayer(props: VideoPlayerProps) {
  const { slotProps, type, videoId, ...rest } = props;
  const { t } = useTranslation();

  const [videoWidth, setVideoWidth] = useState(0);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const onResizeWidth = useCallback(() => {
    setVideoWidth(videoContainerRef.current?.getBoundingClientRect().width || 0);
  }, []);

  useResizeLayoutEffect(onResizeWidth);

  return (
    <ThemeProvider2024>
      <Components.Root ref={videoContainerRef} {...slotProps?.root}>
        {type === "mux" ? (
          <MuxPlayer
            playbackId={videoId}
            accentColor={theme.palette.secondary1[100]}
            style={{ width: videoWidth, aspectRatio: 16 / 9 }}
            {...rest}
          />
        ) : (
          <iframe
            /**
             * {@link https://www.aleksandrhovhannisyan.com/blog/react-iframes-back-navigation-bug/#solution-remount-the-iframe-with-a-key | Fix iframe history bug} */
            key={videoId}
            width={videoWidth}
            height={videoWidth ? (videoWidth / 16) * 9 : undefined}
            src={`https://www.youtube.com/embed/${videoId}`}
            title={t("global.youtube_video_player_label")}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        )}
      </Components.Root>
    </ThemeProvider2024>
  );
}

export default VideoPlayer;
